import React, { useState } from 'react';
import { Pill, ShieldCheck, Mail, Lock, ArrowRight } from 'lucide-react';

interface LoginPageProps {
  onLogin: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('owner@store.com');
  const [password, setPassword] = useState('password');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://brightbrains.pk/store/api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          payload: { email, password },
        }),
      });
      
      const result = await response.json();

      if (result.success) {
        onLogin();
      } else {
        setError(result.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 lg:p-8 font-sans relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-emerald-500 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-blue-600 rounded-full blur-[120px]"></div>
      </div>

      {/* Login Card Container */}
      <div className="w-full max-w-5xl bg-white rounded-[2.5rem] shadow-[0_35px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col lg:flex-row min-h-[600px] relative z-10 animate-in fade-in zoom-in duration-500">
        
        {/* Left Side: Medical Image & Branding */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-[#0f172a] overflow-hidden">
          {/* Medical Image with Overlay */}
          <img 
            src="https://images.unsplash.com/photo-1586015555751-63bb77f4322a?auto=format&fit=crop&q=80&w=1200" 
            alt="Medical Store" 
            className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale-[20%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/80 via-[#0f172a]/60 to-[#0f172a]/90"></div>
          
          <div className="relative z-20 p-12 flex flex-col justify-between w-full">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-emerald-500 p-2.5 rounded-2xl shadow-lg shadow-emerald-500/20">
                  <Pill size={32} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white tracking-tight">
                  Abdullah <span className="text-emerald-400">Medical</span>
                </h1>
              </div>
              <p className="text-slate-300 text-lg leading-relaxed max-w-sm font-light">
                Comprehensive pharmacy management system designed for efficiency, accuracy, and growth.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-xl p-4 rounded-2xl border border-white/10 flex items-center gap-4">
                <div className="bg-emerald-500/20 p-2.5 rounded-xl border border-emerald-500/20">
                  <ShieldCheck className="text-emerald-400" size={24} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Secure Access</p>
                  <p className="text-slate-400 text-xs">End-to-end encrypted data</p>
                </div>
              </div>
              <p className="text-slate-500 text-xs tracking-wide">© 2025 ABDULLAH MEDICAL STORE</p>
            </div>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="flex-1 p-8 lg:p-16 flex flex-col justify-center bg-white">
          <div className="max-w-md mx-auto w-full space-y-10">
            <div className="text-center lg:text-left">
              <h2 className="text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">Welcome Back</h2>
              <p className="text-slate-500 text-lg">Please sign in to your account.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] block ml-1">Email Address</label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 focus:bg-white transition-all text-slate-900 font-medium placeholder:text-slate-400"
                      placeholder="owner@store.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] block ml-1">Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 focus:bg-white transition-all text-slate-900 font-medium placeholder:text-slate-400"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <button type="button" className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors">
                  Forgot Password?
                </button>
              </div>

              {error && (
                <div className="p-4 bg-red-50 text-red-600 text-sm font-semibold rounded-2xl border border-red-100 flex items-center justify-center">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0f172a] text-white py-5 rounded-2xl font-bold text-lg hover:bg-slate-800 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 shadow-2xl shadow-slate-900/20 disabled:opacity-50"
              >
                {loading ? 'Signing In...' : 'Sign In Account'}
                {!loading && <ArrowRight size={22} strokeWidth={2.5} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;